/**
 * The interface glyphs: the drawings any product would need — close, chevrons,
 * arrows, search, settings, the voice controls. Each is a file in
 * src/assets/icons/vesper/, so a drawing is swapped by replacing its file.
 * The game's own glyphs have their registry in components/game/common.
 */

import menu from '../../assets/icons/vesper/menu.svg';
import close from '../../assets/icons/vesper/close.svg';
import chevronDown from '../../assets/icons/vesper/chevron-down.svg';
import chevronRight from '../../assets/icons/vesper/chevron-right.svg';
import arrowRight from '../../assets/icons/vesper/arrow-right.svg';
import arrowLeft from '../../assets/icons/vesper/arrow-left.svg';
import plus from '../../assets/icons/vesper/plus.svg';
import minus from '../../assets/icons/vesper/minus.svg';
import check from '../../assets/icons/vesper/check.svg';
import tick from '../../assets/icons/vesper/tick.svg';
import search from '../../assets/icons/vesper/search.svg';
import filter from '../../assets/icons/vesper/filter.svg';
import settings from '../../assets/icons/vesper/settings.svg';
import info from '../../assets/icons/vesper/info.svg';
import infoLetter from '../../assets/icons/vesper/info-letter.svg';
import warning from '../../assets/icons/vesper/warning.svg';
import exclamation from '../../assets/icons/vesper/exclamation.svg';
import lock from '../../assets/icons/vesper/lock.svg';
import lockBadge from '../../assets/icons/vesper/lock-badge.svg';
import external from '../../assets/icons/vesper/external.svg';
import refresh from '../../assets/icons/vesper/refresh.svg';
import more from '../../assets/icons/vesper/more.svg';
import trash from '../../assets/icons/vesper/trash.svg';
import edit from '../../assets/icons/vesper/edit.svg';
import follow from '../../assets/icons/vesper/follow.svg';
import mic from '../../assets/icons/vesper/mic.svg';
import micOff from '../../assets/icons/vesper/mic-off.svg';
import speaker from '../../assets/icons/vesper/speaker.svg';
import speakerOff from '../../assets/icons/vesper/speaker-off.svg';
import help from '../../assets/icons/vesper/help.svg';
import person from '../../assets/icons/vesper/person.svg';
import disconnect from '../../assets/icons/vesper/disconnect.svg';
import fullscreen from '../../assets/icons/vesper/fullscreen.svg';
import fullscreenExit from '../../assets/icons/vesper/fullscreen-exit.svg';
import grip from '../../assets/icons/vesper/grip.svg';
import sort from '../../assets/icons/vesper/sort.svg';
import pin from '../../assets/icons/vesper/pin.svg';
import tools from '../../assets/icons/vesper/tools.svg';

/**
 * A drawing, as a registry names it. A bare string is not one: only
 * `glyphs()` makes a Glyph, so an icon cannot be pointed at an arbitrary URL
 * and every drawing in use is one a registry lists.
 */
export type Glyph = string & { readonly __glyph: true };

/**
 * A drawing made heavy for a badge's 10px — stroked at 3 or more, or filled —
 * where the family's 1.8 stroke is under a pixel. Icon takes nothing else at
 * xs, so a glyph chosen at runtime is held to it as well as a named one. Only
 * a registry entry marked `heavy()` is one, and design:check reads each file
 * so marked.
 */
export type HeavyGlyph = Glyph & { readonly __heavy: true };

/** A file a registry marks as drawn heavy. */
type HeavyFile = string & { readonly __heavyFile: true };

/** Marks a registry entry as drawn heavy for xs: `tick: heavy(tick)` makes GLYPH.tick a HeavyGlyph. */
export function heavy(file: string): HeavyFile {
  return file as HeavyFile;
}

type Registry<T> = { readonly [K in keyof T]: T[K] extends HeavyFile ? HeavyGlyph : Glyph };

/** Registers drawings: GLYPH, below, and the game's registry are built with it. */
export function glyphs<const T extends Record<string, string>>(files: T): Registry<T> {
  return files as unknown as Registry<T>;
}

/**
 * A drawing laid on its side: one that faces up, turned a quarter clockwise
 * to face right — the airline's plane, flying right in the plane mark
 * (common/plane-glyph.ts's PLANE_MARK). Only `turned()` makes one, and Icon
 * draws it turned. Whatever passes a drawing on to an Icon — a round mark, a
 * notice, a way in's head — passes it whole, so the turn goes with the
 * drawing, and no prop beside it carries it down.
 */
type TurnedGlyph = { readonly glyph: Glyph } & { readonly __turned: true };

/** A drawing that faces up, laid on its side to face right. */
export function turned(glyph: Glyph): TurnedGlyph {
  return { glyph } as TurnedGlyph;
}

/** What an Icon draws: a drawing as its registry names it, or one laid on its side. */
export type Drawing = Glyph | TurnedGlyph;

/**
 * A drawing as the value of `--glyph`, which the glyphMask recipe paints in
 * the colour of the text around it. Icon sets it; so does the one glyph drawn
 * outside React, the globe's locked-target lock — design:check holds every
 * other spelling of it (glyph-painted-by-hand).
 */
export function glyphImage(glyph: Glyph): string {
  return `url("${glyph}")`;
}

export const GLYPH = glyphs({
  menu,
  close,
  chevronDown,
  chevronRight,
  arrowRight,
  arrowLeft,
  plus,
  minus,
  check,
  /** The check drawn heavy, for a badge's 10px, where the family's stroke would vanish. */
  tick: heavy(tick),
  search,
  filter,
  settings,
  info,
  /** Info's letter without its ring, for a mark that is round already: a toast's. */
  infoLetter,
  warning,
  /** The exclamation without a ring or a triangle, for a mark that is round already: a danger toast's. */
  exclamation,
  lock,
  /** The lock drawn heavy and filled, for a badge's 10px: on a locked rail item. */
  lockBadge: heavy(lockBadge),
  external,
  refresh,
  more,
  trash,
  edit,
  follow,
  mic,
  micOff,
  speaker,
  speakerOff,
  help,
  /** Someone with no photo yet: the plain silhouette, filled. */
  person: heavy(person),
  disconnect,
  fullscreen,
  /** Full screen's corners turned in: leaving it. */
  fullscreenExit,
  grip,
  sort,
  /** A place on the map: go to it. */
  pin,
  /** A wrench: the tools a developer opens. */
  tools,
});
